import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import styles from './ReporterEntry.module.css';

function formatLongDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function makeEmptyRow(fields) {
  const row = { _id: `${Date.now()}-${Math.random()}` };
  fields.forEach(f => { row[f.name] = ''; });
  return row;
}

function isGridTemplate(tf) {
  return tf && typeof tf === 'object' && !Array.isArray(tf) && tf.type === 'grid';
}

function makeEmptyGridData(tf) {
  const data = {};
  (tf.rows || []).forEach(row => {
    data[row.id] = {};
    (tf.columns || []).forEach(col => {
      data[row.id][col.id] = '';
    });
  });
  return data;
}

// ── Shared popup header ───────────────────────────────────────────────────────

function PopupHeader({ user, onLogout }) {
  return (
    <div className={styles.popupHeader}>
      <div className={styles.headerLogo}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
        <span>Trucount</span>
      </div>
      <div className={styles.headerRight}>
        <span className={styles.reporterEmail}>{user?.email}</span>
        {onLogout && (
          <button type="button" onClick={onLogout} className={styles.btnHeaderSignOut}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ReporterEntry() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [gridData, setGridData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const fetchContext = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await api.get('/entries/context/');
      if (res.data.success) {
        const ctx = res.data.data;
        setContext(ctx);
        if (ctx.already_submitted) {
          setSubmitted(true);
          setSubmitResult({ already: true });
        } else {
          setSubmitted(false);
          setSubmitResult(null);
          const tf = ctx.template_fields;
          if (isGridTemplate(tf)) {
            setGridData(makeEmptyGridData(tf));
          } else {
            setRows([makeEmptyRow(Array.isArray(tf) ? tf : [])]);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load entry form.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContext();
    function handleVisibility() {
      if (document.visibilityState === 'visible') fetchContext(true);
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchContext]);

  const addRow = useCallback(() => {
    const tf = context?.template_fields;
    if (!Array.isArray(tf)) return;
    setRows(prev => [...prev, makeEmptyRow(tf)]);
  }, [context]);

  const removeRow = useCallback((id) => {
    setRows(prev => prev.length > 1 ? prev.filter(r => r._id !== id) : prev);
  }, []);

  const handleCell = useCallback((rowId, fieldName, value) => {
    setRows(prev => prev.map(r => r._id === rowId ? { ...r, [fieldName]: value } : r));
  }, []);

  function isValid() {
    const tf = context?.template_fields;
    if (!tf) return false;
    if (isGridTemplate(tf)) {
      return tf.rows.every(row =>
        tf.columns.every(col => {
          const val = gridData[row.id]?.[col.id];
          if (val === '' || val === undefined || val === null) return false;
          if (row.type === 'number' || row.type === 'currency') {
            return !isNaN(parseFloat(val)) && parseFloat(val) >= 0;
          }
          return String(val).trim().length > 0;
        })
      );
    }
    if (!Array.isArray(tf) || rows.length === 0) return false;
    return rows.every(row =>
      tf.every(field => {
        const val = row[field.name];
        if (val === '' || val === undefined || val === null) return false;
        if (field.type === 'number' || field.type === 'currency') {
          return !isNaN(parseFloat(val)) && parseFloat(val) >= 0;
        }
        return val.toString().trim().length > 0;
      })
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;
    setSubmitting(true);
    setError('');
    try {
      const tf = context.template_fields;
      let data;

      if (isGridTemplate(tf)) {
        const dataPayload = {};
        tf.rows.forEach(row => {
          dataPayload[row.id] = {};
          tf.columns.forEach(col => {
            const val = gridData[row.id]?.[col.id] ?? '';
            dataPayload[row.id][col.id] =
              (row.type === 'number' || row.type === 'currency') ? parseFloat(val) || 0 : val;
          });
        });
        data = {
          type: 'grid',
          template: {
            columns: tf.columns,
            rows: tf.rows,
            computed_columns: tf.computed_columns || [],
          },
          data: dataPayload,
        };
      } else {
        data = {
          rows: rows.map(row => {
            const rowData = {};
            tf.forEach(field => {
              const val = row[field.name];
              rowData[field.name] = (field.type === 'number' || field.type === 'currency')
                ? parseFloat(val)
                : val;
            });
            return rowData;
          }),
        };
      }

      const res = await api.post('/entries/', { business: context.business_id, data });
      if (res.data.success) {
        setSubmitted(true);
        setSubmitResult(res.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit entry.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.popup}>
          <PopupHeader user={user} onLogout={handleLogout} />
          <div className={styles.loadingBox}>
            <div className={styles.spinner} />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmation / Already submitted ────────────────────────────────────────
  if (submitted) {
    return (
      <div className={styles.overlay}>
        <div className={`${styles.popup} ${styles.popupNarrow}`}>
          <PopupHeader user={user} />
          <div className={styles.confirmBody}>
            <div className={styles.checkmark}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" stroke="#dcfce7" strokeWidth="1" fill="#dcfce7"/>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#16a34a"/>
                <polyline points="22 4 12 14.01 9 11.01" stroke="#16a34a"/>
              </svg>
            </div>
            {submitResult?.already ? (
              <>
                <h2 className={styles.confirmTitle}>Already Submitted</h2>
                <p className={styles.confirmText}>
                  You've already submitted your entry for today.<br />
                  Come back tomorrow for a fresh entry.
                </p>
              </>
            ) : (
              <>
                <h2 className={styles.confirmTitle}>Entry Submitted</h2>
                <p className={styles.confirmText}>
                  Your entry for <strong>{formatDate(submitResult?.entry_date || context?.today)}</strong> has
                  been submitted and locked.
                </p>
                <p className={styles.confirmDetail}>
                  Submitted at {submitResult?.submitted_at
                    ? new Date(submitResult.submitted_at).toLocaleTimeString()
                    : 'now'}
                </p>
              </>
            )}
            <button onClick={handleLogout} className={styles.btnSecondary}>Sign Out</button>
          </div>
        </div>
      </div>
    );
  }

  // ── No template ──────────────────────────────────────────────────────────────
  const tf = context?.template_fields;
  const noTemplate = !tf || (Array.isArray(tf) && tf.length === 0) ||
    (isGridTemplate(tf) && (!tf.columns?.length || !tf.rows?.length));

  if (noTemplate) {
    return (
      <div className={styles.overlay}>
        <div className={`${styles.popup} ${styles.popupNarrow}`}>
          <PopupHeader user={user} onLogout={handleLogout} />
          <div className={styles.confirmBody}>
            <div className={styles.infoIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 className={styles.confirmTitle}>No Report Template</h2>
            <p className={styles.confirmText}>
              Your business doesn't have an active report template yet.
              Please contact the business owner.
            </p>
            <button onClick={handleLogout} className={styles.btnSecondary}>Sign Out</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Grid entry form ──────────────────────────────────────────────────────────
  if (isGridTemplate(tf)) {
    const { columns, rows: tplRows, computed_columns = [] } = tf;
    const hasTotal = computed_columns.some(cc => cc.formula === 'sum');

    function getRowTotal(rowId, rowType) {
      const vals = columns.map(col => parseFloat(gridData[rowId]?.[col.id]) || 0);
      const sum = vals.reduce((a, b) => a + b, 0);
      if (rowType === 'currency') return `$${sum.toFixed(2)}`;
      return sum % 1 === 0 ? String(sum) : sum.toFixed(2);
    }

    function handleGridCell(rowId, colId, value) {
      setGridData(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], [colId]: value },
      }));
    }

    return (
      <div className={styles.overlay}>
        <div className={styles.popup}>
          <PopupHeader user={user} onLogout={handleLogout} />
          <form onSubmit={handleSubmit} className={styles.popupBody}>

            {/* Date + business row */}
            <div className={styles.contextBar}>
              <div className={styles.contextDate}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {formatLongDate(context.today)}
              </div>
              <div className={styles.contextBusiness}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                </svg>
                {context.business_name}
              </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {/* Grid table */}
            <div className={styles.gridSheetWrap}>
              <table className={styles.gridSheet}>
                <thead>
                  <tr>
                    <th className={styles.gridSheetCorner}>Date</th>
                    {columns.map(col => (
                      <th key={col.id} className={styles.gridSheetColHead}>{col.label}</th>
                    ))}
                    {hasTotal && <th className={styles.gridSheetTotalHead}>TOTAL</th>}
                  </tr>
                </thead>
                <tbody>
                  {tplRows.map((tplRow, rowIdx) => (
                    <tr key={tplRow.id} className={styles.gridSheetDataRow}>
                      <td className={styles.gridSheetRowLabel}>{tplRow.label}</td>
                      {columns.map((col, colIdx) => (
                        <td key={col.id} className={styles.gridSheetCell}>
                          <input
                            type={tplRow.type === 'text' ? 'text' : 'number'}
                            inputMode={tplRow.type === 'currency' ? 'decimal' : 'numeric'}
                            step={tplRow.type === 'currency' ? '0.01' : '1'}
                            min={tplRow.type !== 'text' ? '0' : undefined}
                            value={gridData[tplRow.id]?.[col.id] ?? ''}
                            onChange={e => handleGridCell(tplRow.id, col.id, e.target.value)}
                            className={styles.gridSheetInput}
                            placeholder={tplRow.type === 'currency' ? '0.00' : '0'}
                            required
                            autoFocus={rowIdx === 0 && colIdx === 0}
                          />
                        </td>
                      ))}
                      {hasTotal && (
                        <td className={styles.gridSheetTotalCell}>
                          {getRowTotal(tplRow.id, tplRow.type)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Submit */}
            <div className={styles.submitBar}>
              <p className={styles.submitNote}>
                Once submitted, this entry is permanently locked and cannot be edited.
                Only the business owner can amend individual fields.
              </p>
              <button
                type="submit"
                disabled={submitting || !isValid()}
                className={styles.btnSubmit}
              >
                {submitting ? 'Submitting…' : 'Submit Entry'}
              </button>
            </div>

          </form>
        </div>
      </div>
    );
  }

  // ── Flat entry form (legacy) ─────────────────────────────────────────────────
  const fields = tf;
  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <PopupHeader user={user} onLogout={handleLogout} />
        <form onSubmit={handleSubmit} className={styles.popupBody}>

          <div className={styles.contextBar}>
            <div className={styles.contextDate}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {formatLongDate(context.today)}
            </div>
            <div className={styles.contextBusiness}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              </svg>
              {context.business_name}
            </div>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.sheetWrap}>
            <table className={styles.sheet}>
              <thead>
                <tr>
                  {fields.map(f => (
                    <th key={f.name} className={styles.colHeader}>
                      {f.label || f.name}
                    </th>
                  ))}
                  <th className={styles.colHeaderDel} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row._id} className={styles.dataRow}>
                    {fields.map((field, colIdx) => (
                      <td key={field.name} className={styles.cell}>
                        <input
                          type={field.type === 'text' ? 'text' : 'number'}
                          inputMode={field.type === 'currency' ? 'decimal' : field.type === 'number' ? 'numeric' : 'text'}
                          step={field.type === 'currency' ? '0.01' : field.type === 'number' ? '1' : undefined}
                          min={field.type !== 'text' ? '0' : undefined}
                          value={row[field.name] ?? ''}
                          onChange={e => handleCell(row._id, field.name, e.target.value)}
                          className={styles.cellInput}
                          placeholder={field.type === 'currency' ? '0.00' : field.type === 'number' ? '0' : ''}
                          required
                          autoFocus={rows.indexOf(row) === rows.length - 1 && colIdx === 0 && rows.length > 1}
                        />
                      </td>
                    ))}
                    <td className={styles.cellDel}>
                      <button
                        type="button"
                        onClick={() => removeRow(row._id)}
                        className={styles.btnDel}
                        disabled={rows.length === 1}
                        title="Remove row"
                      >×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="button" onClick={addRow} className={styles.btnAddRow}>
            + Add Row
          </button>

          <div className={styles.submitBar}>
            <p className={styles.submitNote}>
              Once submitted, this entry is permanently locked and cannot be edited.
              Only the business owner can amend individual fields.
            </p>
            <button
              type="submit"
              disabled={submitting || !isValid()}
              className={styles.btnSubmit}
            >
              {submitting ? 'Submitting…' : 'Submit Entry'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
